// Local Imports
import type { Editor } from "./editor";
import { ContentKind } from "DrawCore/src/content/kind";
import type { LayoutContent } from "EditorCore/src/content/layout/content";
import type { OverlayKind, RenderStats } from "DrawCore/src/stats";

// Chrome-only performance.memory interface (non-standard)
interface PerformanceMemory {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}

/** Format a byte count as a human-readable string (e.g. "12.3 MB") */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

/** Entity and element statistics computed from the layout model */
interface EntityStats {
  uniqueLayouts: number; // Number of unique Layout model objects
  totalInstances: number; // Sum of instance counts across all layouts
  totalElements: number; // Sum of element counts across all layouts
  sharedTrees: number; // Number of entries in the layoutEntityCache
}

//
// # Counters popup
//
// Display current performance counters in pop-up window.
//
export class CountersDisplay {
  //
  // Reference to the parent `UiState`.
  constructor(readonly editor: Editor) {}

  // The container element that holds both the table and mouse position
  container: HTMLElement | null = null;
  // The inner HTML elements
  table: HTMLElement | null = null;
  mousePosElement: HTMLElement | null = null;
  timingElement: HTMLElement | null = null;
  elemCountElement: HTMLElement | null = null;
  instanceStatsElement: HTMLElement | null = null;
  overlayStatsElement: HTMLElement | null = null;
  memoryElement: HTMLElement | null = null;
  entityStatsElement: HTMLElement | null = null;

  // Enable the counters table display
  enable = () => {
    if (this.container !== null) return;

    // container with perf counter table and
    // mouse coord display as child elements
    this.container = document.createElement("div");
    this.container.style.backgroundColor = "black";
    this.container.style.color = "white";
    this.container.style.border = "thick solid red";
    this.container.style.padding = "5px";
    this.container.style.display = "flex";
    this.container.style.flexDirection = "column";

    // Header explaining this is debug info and how to dismiss it (the
    // keyboard shortcut was removed; this panel is command-palette-only now).
    const headerElement = document.createElement("div");
    headerElement.style.padding = "5px";
    headerElement.style.marginBottom = "5px";
    headerElement.style.fontWeight = "bold";
    headerElement.style.borderBottom = "1px solid #555";
    headerElement.textContent =
      "Debug Counters - Use Command 'Toggle Counters' To Hide";
    this.container.appendChild(headerElement);

    // performance counter table
    this.table = this.editor.perf.table;
    this.editor.perf.update();
    this.container.appendChild(this.table);

    this.mousePosElement = document.createElement("div");
    this.mousePosElement.style.padding = "5px";
    this.mousePosElement.style.marginTop = "5px";
    this.mousePosElement.textContent = "Mouse Position: x: 0.00, y: 0.00";
    this.container.appendChild(this.mousePosElement);

    // Startup time + Elapsed + FPS display
    this.timingElement = document.createElement("div");
    this.timingElement.style.padding = "5px";
    this.timingElement.textContent = "- ms | - ms | - FPS";
    this.container.appendChild(this.timingElement);

    // Element count display
    this.elemCountElement = document.createElement("div");
    this.elemCountElement.style.padding = "5px";
    this.elemCountElement.textContent = "Elements: -";
    this.container.appendChild(this.elemCountElement);

    // Instance rendering stats (per-frame)
    this.instanceStatsElement = document.createElement("div");
    this.instanceStatsElement.style.padding = "5px";
    this.instanceStatsElement.textContent = "Instances: -";
    this.container.appendChild(this.instanceStatsElement);

    // Overlay rendering stats (per-frame). Array and segment counts are
    // rendered inline under the Instances block above — they're subtypes
    // and parts of instance rendering, so nesting them there reads more
    // naturally than as separate top-level rows.
    this.overlayStatsElement = document.createElement("div");
    this.overlayStatsElement.style.padding = "5px";
    this.overlayStatsElement.textContent = "Overlays: -";
    this.container.appendChild(this.overlayStatsElement);

    // Browser memory display (Chrome-only, computed once on open)
    this.memoryElement = document.createElement("div");
    this.memoryElement.style.padding = "5px";
    this.memoryElement.style.borderTop = "1px solid #555";
    this.memoryElement.style.marginTop = "5px";
    this.container.appendChild(this.memoryElement);
    this.updateMemory();

    // Entity stats display (computed once on open)
    this.entityStatsElement = document.createElement("div");
    this.entityStatsElement.style.padding = "5px";
    this.container.appendChild(this.entityStatsElement);
    this.updateEntityStats();

    // Update our neighboring/ linked stuff
    this.editor.overlays.add(this.container);
  };

  // Disable the counters display. Removes it from the DOM if present.
  disable = () => {
    if (this.container) this.container.remove();
    this.container = null;
    this.table = null;
    this.mousePosElement = null;
    this.timingElement = null;
    this.elemCountElement = null;
    this.instanceStatsElement = null;
    this.overlayStatsElement = null;
    this.memoryElement = null;
    this.entityStatsElement = null;
  };

  // Toggle enabled-ness
  toggle = () => {
    if (this.container === null) this.enable();
    else this.disable();
  };

  // Zero the counters.
  clear = () => {
    this.editor.perf.clear();
  };

  // Update mouse position display
  updateMousePos = (x: number, y: number) => {
    if (this.mousePosElement) {
      this.mousePosElement.textContent = `Mouse Position: x: ${x.toFixed(
        2
      )}, y: ${y.toFixed(2)}`;
    }
  };

  // Update elapsed time display
  updateElapsed = (
    elapsedLoad: number,
    elapsedLoadAndRender: number,
    fpsSmoothed: number
  ) => {
    if (this.timingElement) {
      this.timingElement.textContent =
        `Load: ${elapsedLoad.toFixed()} ms | ` +
        `LoadAndRender: ${elapsedLoadAndRender.toFixed()} ms | ` +
        `Avg: ${fpsSmoothed.toFixed(2)} fps`;
    }
  };

  // Update element count display
  updateElemCount = (count: number, max: number) => {
    if (this.elemCountElement) {
      this.elemCountElement.textContent = `Elements: ${count} (Max: ${max})`;
    }
  };

  // Update per-frame rendering stats display.
  //
  // Layout groups by logical hierarchy, with two-space indents on sub-lines:
  //   Elements:   atomic primitive count, with per-bucket + cull breakdowns.
  //   Instances:  cell-level draws, with sprite breakdown and array/segment
  //               sub-groupings nested beneath (they're subtypes and parts
  //               of instance rendering, not peers of it).
  //   Overlays:   per-kind drawn counts. The header total counts only
  //               `drawn` primitives — including `culled` / `sizeCulled`
  //               would inflate the number with work that wasn't done.
  updateRenderStats = (stats: RenderStats) => {
    // Elements — atomic primitive count with bucket + cull breakdowns.
    if (this.elemCountElement) {
      const e = stats.elements;
      this.elemCountElement.innerHTML =
        `Elements: ${stats.elementsDrawn} (Max: ${stats.elementsDrawnMax})<br>` +
        `&nbsp;&nbsp;sprite: ${e.sprite}, vector: ${e.vector}, ` +
        `overlay: ${e.overlay}, direct: ${e.direct}<br>` +
        `&nbsp;&nbsp;culled: ${e.culled} off-screen, ${e.sizeCulled} too-small`;
    }

    // Instances — cell-level draws, with sprite breakdown plus Arrays and
    // Segments as indented logical children.
    if (this.instanceStatsElement) {
      const { instances, arrays, segments } = stats;
      const sprites = instances.spritesCached + instances.spritesBuilt;
      const total =
        sprites + instances.vector + instances.culled + instances.sizeCulled;
      this.instanceStatsElement.innerHTML =
        `Instances: ${total} (${sprites} sprites, ` +
        `${instances.vector} vector, ` +
        `${instances.culled} off-screen, ` +
        `${instances.sizeCulled} too-small)<br>` +
        `&nbsp;&nbsp;sprites: ${instances.spritesCached} cached, ` +
        `${instances.spritesBuilt} built<br>` +
        `&nbsp;&nbsp;Arrays: ${arrays.total} ` +
        `(${arrays.clipped} clipped, ${arrays.fullyCulled} off-screen)<br>` +
        `&nbsp;&nbsp;Segments: ${segments.total} ` +
        `(${segments.drawn} drawn, ${segments.offScreen} off-screen)<br>` +
        `&nbsp;&nbsp;&nbsp;&nbsp;from arrays: ${segments.array.total} ` +
        `(${segments.array.drawn} drawn, ${segments.array.offScreen} off-screen)<br>` +
        `&nbsp;&nbsp;&nbsp;&nbsp;freeform: ${segments.freeform.total} ` +
        `(${segments.freeform.drawn} drawn, ${segments.freeform.offScreen} off-screen)`;
    }

    // Overlays — drawn-only total in the header so it reflects real work;
    // per-kind rows show full drawn/culled/sizeCulled breakdown.
    if (this.overlayStatsElement) {
      const kinds: OverlayKind[] = ["routes", "labels"];
      let drawnTotal = 0;
      const parts: string[] = [];
      for (const k of kinds) {
        const b = stats.overlays[k];
        drawnTotal += b.drawn;
        parts.push(
          `&nbsp;&nbsp;${k}: ${b.drawn} drawn, ${b.culled} off-screen, ${b.sizeCulled} too-small`
        );
      }
      this.overlayStatsElement.innerHTML =
        `Overlays: ${drawnTotal}<br>` + parts.join("<br>");
    }
  };

  // Update browser memory display (one-shot, Chrome-only)
  private updateMemory = () => {
    if (!this.memoryElement) return;

    // performance.memory is a non-standard Chrome extension
    const perf = performance as unknown as {
      memory?: PerformanceMemory;
    };
    const mem = perf.memory;
    if (!mem) {
      this.memoryElement.textContent = "Heap: unavailable (Chrome-only)";
      return;
    }

    const used = formatBytes(mem.usedJSHeapSize);
    const total = formatBytes(mem.totalJSHeapSize);
    const limit = formatBytes(mem.jsHeapSizeLimit);
    this.memoryElement.textContent = `Heap: ${used} used / ${total} allocated / ${limit} limit`;
  };

  // Update entity stats display (one-shot, computed from layout model)
  private updateEntityStats = () => {
    if (!this.entityStatsElement) return;

    const { content } = this.editor;
    if (content.contentKind !== ContentKind.Layout) {
      this.entityStatsElement.textContent = "Entities: (not a layout)";
      return;
    }

    const stats = computeEntityStats(content as LayoutContent);
    // Use innerHTML with <br> for multi-line display in a div
    this.entityStatsElement.innerHTML =
      `Layouts: ${stats.uniqueLayouts} unique, ` +
      `${stats.sharedTrees} cached trees<br>` +
      `Instances: ${stats.totalInstances}<br>` +
      `Elements: ${stats.totalElements}`;
  };
}

/**
 * Walk the LayoutContent's layout map to compute entity statistics.
 * This reads model data only (no entity tree traversal) so it's cheap.
 */
function computeEntityStats(content: LayoutContent): EntityStats {
  const { layouts } = content;

  let totalInstances = 0;
  let totalElements = 0;
  let uniqueLayouts = 0;

  for (const lay of layouts.values()) {
    uniqueLayouts++;
    if (lay.impl) {
      totalInstances += lay.impl.instances.length;
      totalElements += lay.impl.elems.length;
    }
    // Count abstract elements (pins)
    totalElements += lay.abs.pins.length;
  }

  // Count cached entity trees by walking the layouts and checking the cache.
  // WeakMap has no size property, so we count by probing each layout.
  let sharedTrees = 0;
  for (const lay of layouts.values()) {
    if (content.render.layoutEntityCache.get(lay)) {
      sharedTrees++;
    }
  }

  return { uniqueLayouts, totalInstances, totalElements, sharedTrees };
}
